// Phone push (U10) and "Open in Gmail": types and helpers shared by the server and the web (no
// runtime dependencies). Pushes carry a title and a link into MeeMail, never mail content.

/** One browser that receives pushes, as Settings → Notifications lists it. */
export interface PushDeviceView {
  id: string;
  /** "Safari on iPhone". */
  label: string;
  /** The push service's host (the endpoint itself is never shown). */
  service: string;
  createdAt: string;
  lastUsedAt: string | null;
  /** The push service's last refusal, cleared by the next delivery. */
  lastError: string | null;
}

/** `GET /api/push/status`. */
export interface PushStatusView {
  /** False when the server has no usable VAPID keys (see the server log). */
  available: boolean;
  /** The browser subscribes with this (`applicationServerKey`); null when unavailable. */
  publicKey: string | null;
  devices: PushDeviceView[];
  /** Whether something is waiting for quiet hours to end. */
  waiting: boolean;
}

/** `POST /api/push/subscriptions`: the browser's PushSubscription.toJSON(). */
export interface PushSubscriptionInput {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

/** `POST /api/push/test`. */
export interface PushTestResult {
  sent: number;
  /** Devices the push service no longer knows (removed). */
  removed: number;
  /** Devices whose push service couldn't be reached or refused. */
  failed: number;
}

/** In-app path a push opens: a card, a brief, or the feed. Only same-origin paths. */
export function pushPath(target: { cardId?: string | null; briefId?: string | null }): string {
  if (target.cardId) return `/feed/cards/${encodeURIComponent(target.cardId)}`;
  if (target.briefId) return `/brief/${encodeURIComponent(target.briefId)}`;
  return "/feed";
}

/** The Gmail thread id inside a thread key (`${accountId}:${gmailThreadId}`). */
export function gmailThreadIdOf(threadKey: string | null | undefined): string | null {
  if (!threadKey) return null;
  const at = threadKey.indexOf(":");
  const id = at >= 0 ? threadKey.slice(at + 1) : "";
  return /^[A-Za-z0-9_-]+$/.test(id) ? id : null;
}

/** The bare Message-ID (no angle brackets) when it is a plain `local@domain`, else null. */
export function plainMessageId(rfc822MessageId: string | null | undefined): string | null {
  const id = rfc822MessageId?.trim().replace(/^<(.+)>$/, "$1");
  return id && /^[^\s<>"]+@[^\s<>"]+$/.test(id) ? id : null;
}

/**
 * The thread in Gmail on the web. `authuser=<address>` picks the right Google account by address
 * (a `/u/<n>/` index depends on sign-in order, so it opens the wrong mailbox when several are
 * signed in), and Google asks the owner to sign in to that address when it isn't yet. With the
 * original message's Message-ID the link is an exact `rfc822msgid:` search, which finds the
 * message from a fresh tab (an API thread id in `#all/` does not always resolve on a cold link;
 * checked live on the deployed build). Without one, `#all/<thread id>` finds the thread wherever
 * it is filed, archived included.
 */
export function gmailThreadUrl(
  accountEmail: string | null | undefined,
  threadKey: string | null,
  rfc822MessageId?: string | null,
) {
  const id = gmailThreadIdOf(threadKey);
  if (!id) return null;
  const who = accountEmail?.trim() ? `?authuser=${encodeURIComponent(accountEmail.trim())}` : "";
  const messageId = plainMessageId(rfc822MessageId);
  if (messageId)
    return `https://mail.google.com/mail/u/0/${who}#search/${encodeURIComponent(`rfc822msgid:${messageId}`)}`;
  return `https://mail.google.com/mail/${who}#all/${id}`;
}
